<template>
  <v-card>
    <div class="wrap">
      <v-card-title>
        <h2 class="card-title">{{ 'base.add_user' | translate }}</h2>
      </v-card-title>
      <div class="radio-group">
        <label class="radio-label">
          <input
            type="radio"
            name="user_type"
            value="staff"
            v-model="form.user_type"
          />
          <span>{{ 'base.employees' | translate }}</span>
        </label>
        <label class="radio-label">
          <input
            type="radio"
            name="user_type"
            value="consultant"
            v-model="form.user_type"
          />
          <span>{{ 'activities.consultant' | translate }}</span>
        </label>
      </div>
    </div>
    <v-card-content>
      <h3 class="card-subtitle mb-1">{{ 'base.client_info' | translate }}</h3>
      <div class="cols-x">
        <div class="cols-x__2 cols-x__2--offset-md mb-3">
          <v-text-field
            :focus="true"
            :label="$t('base.first_name')"
            v-model="form.first_name"
          />
        </div>
        <div class="cols-x__2 cols-x__2--offset-md mb-3">
          <v-text-field
            :label="$t('base.phone')"
            v-model="form.phone"
          />
        </div>
      </div>
      <div class="cols-x">
        <div class="cols-x__2 cols-x__2--offset-md mb-3">
          <v-text-field
            :label="$t('base.last_name')"
            v-model="form.last_name"
          />
        </div>
        <div class="cols-x__2 cols-x__2--offset-md mb-3">
          <v-text-field
            :label="$t('base.email')"
            v-model="form.email"
          />
        </div>
      </div>
      <div class="cols-x">
        <div class="cols-x__2 cols-x__2--offset-md mb-3">
          <v-text-field
            :label="$t('base.company_name')"
            v-model="form.company_name"
          />
        </div>
        <div class="cols-x__2 cols-x__2--offset-md mb-3">
          <v-text-field
            :label="$t('base.password')"
            v-model="form.password"
          />
        </div>
      </div>
      <div class="cols-x">
        <div class="cols-x__2 cols-x__2--offset-md mb-3 remove-margin-above-mobile">
          <v-text-field
            :label="$t('base.about_client')"
            v-model="form.profile.about"
          />
        </div>
        <div class="cols-x__2 cols-x__2--offset-md">
          <v-select
            item-value="key"
            :label="$t('base.select_lang')"
            :options="languages"
            v-model="form.profile.language"
          />
        </div>
      </div>
    </v-card-content>
    <v-divider secondary />

    <v-card-content>
      <h3 class="card-subtitle mb-1">{{ 'base.profeed_info' | translate }}</h3>
      <div class="cols-x">
        <div class="cols-x__2 cols-x__2--offset-md mb-3 remove-margin-above-mobile">
          <v-multiselect-with-api-search
            ref="clientSearch"
            :label="`${$t('base.clients')} (${clients_ids.length}/${clientList.length})`"
            :options="clientList"
            item-name="display_name"
            item-subname="name"
            v-model="clients_ids"
            :loading="clientsLoading"
            :comparator="clientComparator"
            @search="onClientSearch"
            @scroll-complete="onClientScrollComplete"
          />
        </div>
        <div class="cols-x__2 cols-x__2--offset-md">
          <v-multiselect-with-api-search
            ref="farmSearch"
            :label="`${$t('base.farms')} (${form.farms_ids.length}/${filteredFarms.length})`"
            :options="filteredFarms"
            item-name="name"
            v-model="form.farms_ids"
            :loading="farmsLoading"
            :comparator="farmComparator"
            @search="onFarmSearch"
            @scroll-complete="onFarmScrollComplete"
            :disabled="!clients_ids.length"
          />
        </div>
      </div>
    </v-card-content>
    <v-card-actions>
      <v-btn
        :loading="loading"
        @click="onCreate"
      >
        {{ 'base.save' | translate }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'CreateConsultantModal',

  async mounted () {
    const params = {limit: this.limit}
    const result = await this.loadClientList(params)

    if (result && result.next) {
      this.hasMoreClients = true
    } else {
      this.hasMoreClients = false
    }
  },

  data: () => ({
    form: {
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      phone: '',
      user_type: 'consultant',
      farms_ids: [],
      company_name: '',
      profile: {
        about: '',
        language: null
      }
    },
    clients_ids: [],
    currentSearchTerm: '',
    currentFarmSearchTerm: '',
    languages: [
      { id: 0, name: 'Українська', key: 'uk' },
      { id: 1, name: 'Русский', key: 'ru' },
      { id: 2, name: 'Polski', key: 'pl' },
      { id: 3, name: 'English', key: 'en' },
      { id: 4, name: 'Eesti', key: 'et' },
      { id: 5, name: 'Ελληνικά', key: 'el' },
      { id: 6, name: 'Español', key: 'es' },
      { id: 7, name: 'Español (US)', key: 'es-us' },
      { id: 8, name: 'Netherlands', key: 'nl' },
      { id: 9, name: 'Deutsch', key: 'de' },
      { id: 10, name: 'Français', key: 'fr' },
      { id: 11, name: 'Italiano', key: 'it' },
      { id: 12, name: 'Română', key: 'ro' },
      { id: 13, name: 'USA', key: 'us' },
    ],
    loading: false,
    clientsLoading: false,
    farmsLoading: false,
    hasMoreClients: true,
    hasMoreFarms: true,
    limit: 1000
  }),

  computed: {
    ...mapGetters('dashboard', [
      'clientList',
      'clientsFarms'
    ]),

    filteredFarms () {
      if (!this.clients_ids.length) {
        return []
      }

      const selectedClientIds = this.clients_ids.map(client => client.id)

      return this.clientsFarms.filter(farm => {
        return selectedClientIds.includes(farm.client)
      })
    }
  },

  methods: {
    clientComparator (selected, option) {
      return selected.id === option.id
    },

    farmComparator (selected, option) {
      return selected.id === option.id
    },

    async onClientSearch (searchTerm) {
      this.clientsLoading = true
      this.currentSearchTerm = searchTerm
      const params = searchTerm ? { name: searchTerm, limit: this.limit } : {}
      const result = await this.loadClientList(params)

      if (result && result.next) {
        this.hasMoreClients = true
      } else {
        this.hasMoreClients = false
      }

      this.clientsLoading = false
    },

    async onClientScrollComplete () {
      if (!this.hasMoreClients || this.clientsLoading) {
        return
      }

      this.clientsLoading = true

      const currentLength = this.clientList.length
      const params = {
        offset: currentLength,
        limit: this.limit
      }

      if (this.currentSearchTerm) {
        params.name = this.currentSearchTerm
      }

      try {
        const result = await this.loadClientList(params)

        if (!result || !result.data || result.data.length === 0 || !result.next) {
          this.hasMoreClients = false
        }
      } catch (error) {
        console.error('Error loading clients:', error)
        this.hasMoreClients = false
      }

      this.clientsLoading = false
    },

    async onFarmSearch (searchTerm) {
      if (!this.clients_ids.length) {
        return
      }

      this.farmsLoading = true
      this.currentFarmSearchTerm = searchTerm

      const clientIds = this.clients_ids.map(client => client.id)
      const params = {
        client: clientIds,
        limit: this.limit
      }

      if (searchTerm) {
        params.name = searchTerm
      }

      try {
        const result = await this.loadClientsFarms(params)

        if (result && result.next) {
          this.hasMoreFarms = true
        } else {
          this.hasMoreFarms = false
        }
      } catch (error) {
        console.error('Error searching farms:', error)
        this.hasMoreFarms = false
      }

      this.farmsLoading = false
    },

    async onFarmScrollComplete () {
      if (!this.hasMoreFarms || this.farmsLoading || !this.clients_ids.length) {
        return
      }

      this.farmsLoading = true

      const currentLength = this.clientsFarms.length
      const clientIds = this.clients_ids.map(client => client.id)
      const params = {
        client: clientIds,
        limit: this.limit,
        offset: currentLength
      }

      if (this.currentFarmSearchTerm) {
        params.name = this.currentFarmSearchTerm
      }

      try {
        const result = await this.loadClientsFarms(params)

        if (!result || !result.data || result.data.length === 0 || !result.next) {
          this.hasMoreFarms = false
        }
      } catch (error) {
        console.error('Error loading farms:', error)
        this.hasMoreFarms = false
      }

      this.farmsLoading = false
    },

    async loadFarms () {
      if (!this.clients_ids.length) {
        this.form.farms_ids = []
        this.hasMoreFarms = false
        return
      }

      const clientIds = this.clients_ids.map(client => client.id)
      const params = {
        client: clientIds,
        limit: this.limit
      }

      try {
        this.farmsLoading = true
        const result = await this.loadClientsFarms(params)

        if (result && result.next) {
          this.hasMoreFarms = true
        } else {
          this.hasMoreFarms = false
        }
      } catch (error) {
        console.error('Error loading farms:', error)
        this.$toasted.show('Error loading farms!')
        this.hasMoreFarms = false
      } finally {
        this.farmsLoading = false
      }
    },
    removeFarmsOfUncheckedClients (oldClients, newClients) {
      const oldClientIds = oldClients.map(client => client.id)
      const newClientIds = newClients.map(client => client.id)
      const uncheckedClientIds = oldClientIds.filter(id => !newClientIds.includes(id))
      this.form.farms_ids = this.form.farms_ids.filter(farm => {
        return !uncheckedClientIds.includes(farm.client)
      })
    },

    filterFarmsBySelectedClients () {
      if (!this.clients_ids.length) {
        this.form.farms_ids = []
        this.hasMoreFarms = false
        return
      }

      const selectedClientIds = this.clients_ids.map(client => client.id)

      this.form.farms_ids = this.form.farms_ids.filter(farm => {
        return selectedClientIds.includes(farm.client)
      })
    },

    ...mapActions('dashboard', [
      'loadClientList',
      'loadClientsFarms',
      'createUser'
    ]),

    async onCreate () {
      if (this.validateForm()) {
        const payload = {
          email: this.form.email,
          password: this.form.password,
          first_name: this.form.first_name,
          last_name: this.form.last_name,
          phone: this.form.phone,
          is_active: true,
          user_type: this.form.user_type,
          clients: this.clients_ids.map(item => ({ id: item.id })),
          farms: this.form.farms_ids.map(item => ({ id: item.id })),
          display_name: this.form.company_name,
          profile: {
            about: this.form.profile.about,
            language: this.form.profile.language,
          }
        }
        this.loading = true
        const errors = await this.createUser(payload)
        this.loading = false
        if (!errors) {
          this.$emit('close')
        } else {
          if (errors.email) {
            this.$toasted.show('Incorrect email!')
          } else {
            if (errors.phone) {
              this.$toasted.show('Incorrect phone!')
            } else {
              if (errors.password) {
                this.$toasted.show('Incorrect password!')
              } else {
                this.$toasted.show('Something went wrong!')
              }
            }
          }
        }
      }
    },

    validateForm () {
      if (!this.form.email) {
        this.$toasted.show('Email required!')
        return false
      }
      if (!this.form.password) {
        this.$toasted.show('Password required!')
        return false
      }
      if (this.form.password.length < 8) {
        this.$toasted.show('Password length must be equal or more than 8 symbols')
        return false
      }
      if (!this.form.first_name) {
        this.$toasted.show('Firstname required!')
        return false
      }
      if (!this.form.last_name) {
        this.$toasted.show('Lastname required!')
        return false
      }
      if (!this.form.phone) {
        this.$toasted.show('Phone required!')
        return false
      }
      if (!this.form.company_name) {
        this.$toasted.show('Company name is required!')
        return false
      }
      if (!this.form.profile.language) {
        this.$toasted.show('Language is required!')
        return false
      }
      return true
    }
  },

  watch: {
    clients_ids: {
      handler (newClients, oldClients) {
        if (!newClients.length) {
          this.form.farms_ids = []
          this.hasMoreFarms = false
          return
        }

        if (oldClients && oldClients.length > newClients.length) {
          this.removeFarmsOfUncheckedClients(oldClients, newClients)
        }

        this.loadFarms()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.wrap {
  display: flex;
  align-items: center;
  gap: 40px;
}

.radio-group {
  padding: 2.4rem 2.4rem 0;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  gap: 20px;
  position: relative;
}
.radio-group::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -10px;
  height: 2px;
  display: block;
  background: #e2e2e2;
}
.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
}
.radio-label input {
  margin-top: -3px;
}
.radio-group {
  display: flex;
  gap: 20px;
}

.radio-label {
  position: relative;
  padding-left: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.radio-label input[type="radio"] {
  display: none;
}

.radio-label::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  border: 1px solid #ccc;
  border-radius: 50%;
  background-color: #fff;
}

.radio-label input[type="radio"]:checked + span::before {
  content: '';
  position: absolute;
  left: 3px;
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #4CAF50;
}
@media screen and (max-width: 767px) {
  .wrap {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 10px;
    gap: unset;
  }
  .radio-group::after {
    display: none;
  }
}
</style>